"use client";

import { c as _c } from "react/compiler-runtime";
import { useUserPreferences } from "@/hooks/preference/use-user-preferences";
import { useAuth } from "@/hooks/use-auth";
import { useRelationship } from "@/hooks/use-relationship";
import { useMemo } from "react";
import { PROMOTIONAL_CARDS_REGISTRY } from "./registry";
import { CardVisibilityContext, PromotionalCardConfig } from "./types";

/**
 * Get a random selection of cards based on priority weights
 */
export function getRandomCards(count: number, sourceCards: PromotionalCardConfig[]): PromotionalCardConfig[] {
  if (sourceCards.length === 0) return [];
  if (sourceCards.length <= count) return [...sourceCards];

  // Weighted random selection based on priority
  const selected: PromotionalCardConfig[] = [];
  const available = [...sourceCards];
  while (selected.length < count && available.length > 0) {
    const totalWeight = available.reduce((sum, card) => sum + card.priority, 0);
    let random = Math.random() * totalWeight;
    for (let i = 0; i < available.length; i++) {
      random -= available[i].priority;
      if (random <= 0) {
        selected.push(available[i]);
        available.splice(i, 1);
        break;
      }
    }
  }
  return selected;
}

/**
 * Hook to manage and filter promotional cards based on current context.
 *
 * @param pageType - The current page type (search, sup, or project)
 * @returns Object containing eligible cards and utility functions
 */
export function usePromotionalCards(pageType) {
  const $ = _c(11);
  const {
    isAuthenticated
  } = useAuth();
  const {
    preferences
  } = useUserPreferences();
  const {
    isBuyer
  } = useRelationship();
  const hasPreferences = preferences.length > 0;
  let t0;
  if ($[0] !== hasPreferences || $[1] !== isAuthenticated || $[2] !== isBuyer || $[3] !== pageType) {
    t0 = {
      isAuthenticated,
      hasPreferences,
      isBuyer,
      pageType
    };
    $[0] = hasPreferences;
    $[1] = isAuthenticated;
    $[2] = isBuyer;
    $[3] = pageType;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const context = t0;
  let t1;
  if ($[5] !== context || $[6] !== pageType) {
    t1 = PROMOTIONAL_CARDS_REGISTRY.filter(card => {
      if (!card.pages.includes(pageType)) {
        return false;
      }
      return card.visibilityConditions(context);
    });
    $[5] = context;
    $[6] = pageType;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const potentialCards = t1;
  let t2;
  if ($[8] !== context || $[9] !== potentialCards) {
    t2 = {
      potentialCards,
      getRandomCards,
      context
    };
    $[8] = context;
    $[9] = potentialCards;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}